import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { IBANValidator } from '../utils/ibanValidator';
import i18n from '../i18n';

interface IBANInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean | null) => void;
  placeholder?: string;
  required?: boolean;
  allowedCountries?: string[]; // Optional: restrict to specific countries
  showCountryCode?: boolean;
  showDetailedErrors?: boolean;
  // #108 (S3): the control id; the label (IbanForm / IbanModal) points at it
  id?: string;
}

export const IBANInput: React.FC<IBANInputProps> = ({
  value,
  onChange,
  onValidationChange,
  placeholder = i18n.t('ibanForm.enterIban'),
  required = false,
  allowedCountries = [],
  showDetailedErrors = false,
  id = 'iban-input',
}) => {
  const [touched, setTouched] = useState(false);

  // Memoized validation details
  const validationDetails = useMemo(() => {
    if (!value.trim()) {
      return {
        isValid: null,
        countryCode: null,
        length: 0,
        formatted: '',
        errors: [],
      };
    }

    const details = IBANValidator.getValidationDetails(value);

    // Check country restrictions
    if (allowedCountries.length > 0 && details.countryCode) {
      const isAllowedCountry = allowedCountries.includes(details.countryCode);
      if (!isAllowedCountry) {
        details.isValid = false;
        details.errors.push(`Country ${details.countryCode} is not allowed`);
      }
    }

    return details;
  }, [value, allowedCountries]);

  useEffect(() => {
    onValidationChange?.(validationDetails.isValid);
  }, [validationDetails.isValid, onValidationChange]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Only allow alphanumeric characters and spaces
      if (/^[A-Za-z0-9\s]*$/.test(inputValue)) {
        // Format as user types
        const formatted = IBANValidator.format(inputValue);
        onChange(formatted);
      }
    },
    [onChange]
  );

  const handleBlur = () => {
    setTouched(true);
  };

  const getErrorMessage = () => {
    if (!showDetailedErrors && validationDetails.errors.length > 0) {
      return i18n.t('ibanForm.enterValid');
    }
    return validationDetails.errors.join('. ');
  };

  // #108 (S3): the error / help texts are linked to the control (the old
  // is-valid / is-invalid border classes go; aria-invalid drives the border)
  const invalid = touched && validationDetails.isValid === false;
  const valid = touched && validationDetails.isValid === true;
  const countriesId = `${id}-countries`;
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;
  const describedBy =
    [
      allowedCountries.length > 0 ? countriesId : '',
      invalid ? errorId : '',
      valid ? helpId : '',
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <>
      <input
        id={id}
        type="text"
        className="xc-input"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder ?? 'Enter IBAN number'}
        maxLength={38} // Max formatted IBAN length with spaces
        required={required}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={invalid}
        aria-describedby={describedBy}
      />

      {allowedCountries.length > 0 && (
        <p className="xc-field__help" id={countriesId}>
          ({i18n.t('ibanForm.allowedCountry')} {allowedCountries.join(', ')})
        </p>
      )}

      {/* Error feedback */}
      {invalid && (
        <p className="xc-field__error" id={errorId} role="alert">
          {getErrorMessage()}
        </p>
      )}

      {/* Success feedback */}
      {valid && (
        <p className="xc-field__help" id={helpId}>
          {i18n.t('ibanForm.validIban')}
        </p>
      )}
    </>
  );
};
